// Saisie RH des congés (M11) : nouvelle demande (agent, règle de l'année, période en demi-journées, motif
// chiffré) et règles de décompte versionnées. Aucune règle officielle n'est préchargée : chaque règle est
// saisie par la collectivité et reste à confirmer par l'administration.
import { LIBELLES_TYPE_CONGE, TYPES_CONGE } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, Panneau, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerRegle, demanderConge, INVALIDER_PERSONNEL, useAgents, useRegles, type RegleConge } from './api';
import { aujourdhuiIso, JOURS_SEMAINE } from './logique';
import styles from '../proprete/proprete.module.css';

const DEMI = [{ valeur: 'matin', libelle: 'Matin' }, { valeur: 'apres_midi', libelle: 'Après-midi' }];

export function NouvelleDemande() {
  const jour = aujourdhuiIso();
  const [v, setV] = useState({ agent_id: '', regle_id: '', debut: jour, debut_demi_journee: 'matin', fin: jour, fin_demi_journee: 'apres_midi', motif: '' });
  const annee = Number(v.debut.slice(0, 4)) || Number(jour.slice(0, 4));
  const agents = useAgents('');
  const regles = useRegles(annee);
  const maj = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((x) => ({ ...x, [k]: e.target.value }));
  const action = useAction({
    executer: () => demanderConge({ agent_id: v.agent_id, regle_id: v.regle_id, periode: { debut: v.debut, debut_demi_journee: v.debut_demi_journee, fin: v.fin, fin_demi_journee: v.fin_demi_journee }, motif: v.motif.trim() || undefined }),
    succes: 'Demande enregistrée : elle attend la décision du chef de service.',
    invalider: INVALIDER_PERSONNEL,
    apres: () => setV((x) => ({ ...x, motif: '' })),
  });
  const aucuneRegle = regles.isSuccess && regles.data.elements.length === 0;
  return (
    <Panneau titre="Nouvelle demande de congé" niveau={2}>
      {aucuneRegle ? <Bandeau type="attention">Aucune règle de décompte n&apos;est saisie pour {annee} : une demande ne peut pas être enregistrée sans règle explicite.</Bandeau> : null}
      <div className={styles.grilleFormulaire}>
        <Selection libelle="Agent" options={(agents.data?.elements ?? []).map((a) => ({ valeur: a.id, libelle: `${a.nom} ${a.prenoms} (${a.matricule})` }))} optionVide="Choisir un agent" value={v.agent_id} onChange={maj('agent_id')} />
        <Selection libelle={`Règle de décompte ${annee}`} options={(regles.data?.elements ?? []).map((r) => ({ valeur: r.id, libelle: `${r.libelle} (${LIBELLES_TYPE_CONGE[r.type]}, version ${r.version})` }))} optionVide="Choisir une règle" value={v.regle_id} onChange={maj('regle_id')} />
        <Champ libelle="Début" type="date" value={v.debut} onChange={maj('debut')} required />
        <Selection libelle="Demi-journée de début" options={DEMI} value={v.debut_demi_journee} onChange={maj('debut_demi_journee')} />
        <Champ libelle="Fin" type="date" value={v.fin} onChange={maj('fin')} required />
        <Selection libelle="Demi-journée de fin" options={DEMI} value={v.fin_demi_journee} onChange={maj('fin_demi_journee')} />
        <Champ libelle="Motif (chiffré, visible des seuls RH habilités)" value={v.motif} onChange={maj('motif')} facultatif />
      </div>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      <Bouton variante="principal" chargement={action.enCours} disabled={!v.agent_id || !v.regle_id} onClick={() => action.lancer(undefined)}>Enregistrer la demande</Bouton>
    </Panneau>
  );
}

export function ReglesConges() {
  const { contexte } = useSession();
  const [annee, setAnnee] = useState(Number(aujourdhuiIso().slice(0, 4)));
  const regles = useRegles(annee);
  const [v, setV] = useState({ libelle: '', type: 'annuel', jours: [1, 2, 3, 4, 5], exclus: '', solde_requis: true, reference: '' });
  const exclus = v.exclus.split(/[\s,;]+/).filter(Boolean);
  const action = useAction({
    executer: () => creerRegle({ libelle: v.libelle, type: v.type, annee, jours_decomptes: v.jours, jours_exclus: exclus, solde_requis: v.solde_requis, reference_administrative: v.reference }),
    succes: (r) => `Règle enregistrée en version ${r.version}.`,
    invalider: INVALIDER_PERSONNEL,
  });
  const colonnes: ColonneTableau<RegleConge>[] = [
    { id: 'libelle', entete: 'Règle', enTeteLigne: true, cellule: (r) => r.libelle },
    { id: 'type', entete: 'Type', cellule: (r) => LIBELLES_TYPE_CONGE[r.type] },
    { id: 'version', entete: 'Version', numerique: true, cellule: (r) => r.version },
    { id: 'jours', entete: 'Jours décomptés', cellule: (r) => r.jours_decomptes.map((j) => JOURS_SEMAINE[j - 1]).join(', ') },
    { id: 'exclus', entete: 'Dates exclues', numerique: true, cellule: (r) => r.jours_exclus.length },
    { id: 'solde', entete: 'Solde exigé', cellule: (r) => (r.solde_requis ? 'oui' : 'non') },
    { id: 'reference', entete: 'Référence administrative', cellule: (r) => r.reference_administrative },
  ];
  const basculer = (j: number) => setV((x) => ({ ...x, jours: x.jours.includes(j) ? x.jours.filter((y) => y !== j) : [...x.jours, j].sort() }));
  return (
    <>
      <Bandeau type="info">Chaque règle est saisie par la collectivité, versionnée et figée avec les demandes qui l&apos;utilisent. Règle à confirmer par l&apos;administration : aucune valeur n&apos;est présentée comme règle officielle.</Bandeau>
      <Champ libelle="Année" type="number" min={2000} max={2200} value={annee} onChange={(e) => setAnnee(Number(e.target.value))} />
      <Panneau titre={`Règles ${annee}`} niveau={2}>
        <p>{regles.isSuccess && regles.data.elements.length === 0 ? `Aucune règle saisie pour ${annee}.` : null}</p>
        {regles.data?.elements.length ? <Tableau legende={`Règles de décompte ${annee}`} colonnes={colonnes} lignes={regles.data.elements} cleLigne={(r) => r.id} /> : null}
        {regles.error ? <Bandeau type="erreur">Les règles n&apos;ont pas pu être lues.</Bandeau> : null}
      </Panneau>
      {peut(contexte, 'personnel_sensible', 'modifier') ? (
        <Panneau titre="Nouvelle version de règle" niveau={2}>
          <div className={styles.grilleFormulaire}>
            <Champ libelle="Libellé" value={v.libelle} onChange={(e) => setV((x) => ({ ...x, libelle: e.target.value }))} required />
            <Selection libelle="Type" options={TYPES_CONGE.map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_CONGE[t] }))} value={v.type} onChange={(e) => setV((x) => ({ ...x, type: e.target.value }))} />
            <Champ libelle="Référence administrative" value={v.reference} onChange={(e) => setV((x) => ({ ...x, reference: e.target.value }))} required />
            <Champ libelle="Dates exclues (AAAA-MM-JJ, séparées par des virgules)" value={v.exclus} onChange={(e) => setV((x) => ({ ...x, exclus: e.target.value }))} facultatif />
          </div>
          <fieldset>
            <legend>Jours de la semaine décomptés</legend>
            {JOURS_SEMAINE.map((j, i) => <CaseACocher key={j} libelle={j} checked={v.jours.includes(i + 1)} onChange={() => basculer(i + 1)} />)}
          </fieldset>
          <CaseACocher libelle="Solde exigé (la validation RH débite le solde)" checked={v.solde_requis} onChange={(e) => setV((x) => ({ ...x, solde_requis: e.target.checked }))} />
          {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
          <Bouton variante="principal" chargement={action.enCours} disabled={v.libelle.trim().length < 3 || v.reference.trim().length < 3 || v.jours.length === 0} onClick={() => action.lancer(undefined)}>Enregistrer la règle</Bouton>
        </Panneau>
      ) : null}
    </>
  );
}
